/**
 * Một câu tiếng Việt đọc được cho lỗi API, thay vì một mã máy.
 *
 * Vì sao phải có chỗ này: backend trả về CẢ HAI khoá cho một lỗi 422 -
 *
 *     { "errors": { "phone": "phoneAlreadyExists" }, "error": "phoneAlreadyExists" }
 *
 * - `error`  : mã lỗi, máy tra bảng được;
 * - `errors` : lời nhắn theo từng trường, có thể là mã của mình hoặc một câu tiếng
 *              Anh dựng sẵn của class-validator.
 *
 * Mã lỗi chung của HTTP ("unprocessableEntity", "badRequest") KHÔNG có trong bảng
 * dưới, có chủ ý: gặp nó thì rơi xuống `errors` để hiện lý do thật. Màn đăng ký từng
 * báo đúng chữ "unprocessableEntity" cho người dùng cuối vì đọc thẳng `error` rồi
 * dừng, trong khi lý do nằm ngay bên cạnh.
 */

const THONG_DIEP: Record<string, string> = {
  phoneAlreadyExists: 'Số điện thoại này đã được đăng ký',
  emailAlreadyExists: 'Email này đã được đăng ký',
  phoneNotExists: 'Số điện thoại này chưa được đăng ký',
  /*
   * Một câu duy nhất cho mọi kiểu đăng nhập hỏng, và cố ý KHÔNG nói hỏng ở đâu:
   * tách "chưa đăng ký" khỏi "sai mật khẩu" là cho người lạ một cách gõ thử số
   * điện thoại / email của người khác để biết họ có tài khoản ở đây hay không.
   * Máy chủ cũng trả về đúng một mã cho cả hai (invalidCredentials).
   */
  invalidCredentials: 'Số điện thoại/email hoặc mật khẩu không đúng',
  // Mã cũ của màn đăng nhập, giữ lại để bản front office này chạy được cả với
  // máy chủ chưa kịp cập nhật. Máy chủ mới không trả nó nữa.
  incorrectPassword: 'Số điện thoại/email hoặc mật khẩu không đúng',
  incorrectOldPassword: 'Mật khẩu hiện tại không đúng',
  missingOldPassword: 'Vui lòng nhập mật khẩu hiện tại',
  mustBeNotEmpty: 'Vui lòng điền đủ thông tin',
  invalidOTP: 'Mã OTP không đúng hoặc đã hết hạn',
  // Vẫn là câu chung chung, vì mã này còn do những endpoint khác trả về (ví dụ
  // sửa hồ sơ với một phiên trỏ tới tài khoản đã bị xoá). Màn đăng nhập không
  // còn nhận nó nữa.
  userNotFound: 'Không tìm thấy tài khoản',
  hashNotFound: 'Liên kết đã hết hạn, vui lòng yêu cầu lại',
  exceedAttemptsToday: 'Bạn đã thử quá số lần cho phép hôm nay',
  emailNotExists: 'Email này chưa được đăng ký',
  cannotSendEmail: 'Không gửi được email, vui lòng thử lại sau',
  otpRequestTooSoon: 'Vui lòng đợi một phút trước khi xin mã mới',
  useGoogleSignIn: 'Tài khoản này đăng nhập bằng Google, vui lòng bấm "Tiếp tục với Google"',
  invalidGoogleToken: 'Phiên đăng nhập Google đã hết hạn, vui lòng thử lại',
  googleSignInNotConfigured: 'Đăng nhập Google chưa sẵn sàng, vui lòng đăng nhập bằng mật khẩu',
  googleAccountWithoutEmail: 'Tài khoản Google này không có email, không dùng được để đăng nhập',
  emailNotEditableForGoogleAccount: 'Email của tài khoản Google do Google quản lý, không sửa được',
};

export const MAC_DINH = 'Không kết nối được máy chủ, vui lòng thử lại';

/**
 * Thứ tự ưu tiên, và lý do của từng bậc:
 *
 *   1. mã lỗi ĐÃ DỊCH   - câu tiếng Việt luôn hơn mọi thứ khác;
 *   2. lời nhắn theo trường - cụ thể hơn một mã chung như "unprocessableEntity",
 *                             kể cả khi còn là tiếng Anh;
 *   3. mã lỗi chưa dịch  - xấu, nhưng còn lần ra được;
 *   4. câu mặc định      - khi response không có gì (mất mạng, CORS, 502...).
 */
export const apiErrorMessage = (e: any, macDinh: string = MAC_DINH): string => {
  const data = e?.response?.data;
  if (!data) {
    return macDinh;
  }

  const code = typeof data.error === 'string' ? data.error.trim() : '';
  if (code && THONG_DIEP[code]) {
    return THONG_DIEP[code];
  }

  if (data.errors && typeof data.errors === 'object') {
    const chiTiet = Object.values(data.errors)
      .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
      .map((v) => THONG_DIEP[v.trim()] ?? v.trim());
    if (chiTiet.length) {
      return chiTiet.join('\n');
    }
  }

  return code || macDinh;
};
